/**
 * <tws-plan-cards> — collapses the pricing cards into an accordion on mobile.
 *
 * Progressive enhancement: without this the cards render fully expanded, which
 * is the desktop layout and remains usable at any width. Once it takes over it
 * marks itself `is-enhanced`, and the stylesheet collapses all but the open
 * card below the plan grid's single-column breakpoint.
 *
 * The one-time entry row is the last card in the set, so choosing it collapses
 * the plans just like picking a tier does.
 */
const NARROW = '(max-width: 749px)';

class TwsPlanCards extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-plan-card]'));
    this.toggles = Array.from(this.querySelectorAll('[data-plan-toggle]'));

    if (this.toggles.length === 0) return;

    // The monthly / annual switch can hide the open card, so follow it too.
    this.periods = Array.from(this.parentElement?.querySelectorAll('.tws-pricing__radio') || []);

    this.narrow = window.matchMedia(NARROW);
    this.onClick = this.onClick.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onPeriod = this.onPeriod.bind(this);

    this.toggles.forEach((toggle) => toggle.addEventListener('click', this.onClick));
    this.periods.forEach((radio) => radio.addEventListener('change', this.onPeriod));
    this.narrow.addEventListener('change', this.onBreakpoint);

    this.classList.add('is-enhanced');
    this.open(this.cards.findIndex((card) => card.hasAttribute('data-plan-open')));
  }

  disconnectedCallback() {
    this.toggles.forEach((toggle) => toggle.removeEventListener('click', this.onClick));
    this.periods.forEach((radio) => radio.removeEventListener('change', this.onPeriod));
    if (this.narrow) this.narrow.removeEventListener('change', this.onBreakpoint);
  }

  onPeriod() {
    // The card that was open may belong to the period just switched away from,
    // so open the first one still on show.
    this.open(this.cards.findIndex((card) => card.offsetParent !== null));
  }

  onClick(event) {
    const card = event.currentTarget.closest('[data-plan-card]');
    const index = this.cards.indexOf(card);

    // One card is always open: re-tapping it does nothing, opening another closes it.
    this.open(index);
  }

  onBreakpoint() {
    // Wider layouts show every card in full, so the state has to be re-synced.
    this.open(this.cards.findIndex((card) => card.classList.contains('is-open')));
  }

  open(index) {
    this.cards.forEach((card, i) => {
      const isOpen = i === index;
      card.classList.toggle('is-open', isOpen);
      // Above the breakpoint nothing is collapsed, so every card reads as open.
      card.querySelector('[data-plan-toggle]')?.setAttribute('aria-expanded', String(isOpen || !this.narrow.matches));
    });
  }
}

if (!customElements.get('tws-plan-cards')) {
  customElements.define('tws-plan-cards', TwsPlanCards);
}
